import axios, { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { SuccessResponse } from "../types/interfaces";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_SERVER_DOMAIN}/api/v1`,
  timeout: 20000,
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem("token");
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      } else {
        toast.error(error.response?.data?.message, {
          onClick: () => {
            window.location.href = "/login";
          },
        });
      }
    }
    return Promise.reject(error);
  }
);

class ApiService {
  async Login(credentials: {
    email: string;
    password: string;
  }): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post("/signin", credentials);
  }

  async Register(credentials: {
    email: string;
    password: string;
    name: string;
  }): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post("/signup", credentials);
  }

  async ForgotPassword(
    email: string
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post("/forgot-password", { email });
  }

  async ResetPassword(data: {
    newPassword: string;
    token: string;
  }): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post(`/password/reset/${data.token}`, {
      newPassword: data.newPassword,
    });
  }

  async Logout(): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get("/logout");
  }

  async GetUser(): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get("/user");
  }

  async GetUserProfile(): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get("/profile");
  }

  async SaveQRCode(
    data: FormData
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post("/add-qr", data);
  }

  async UpdateQRCode(
    data: FormData,
    id: string
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.put(`/update-qr/${id}`, data);
  }

  async FetchQRCode(): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get("/qrs");
  }

  async GetQrCodeById(
    id: string
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get(`/qr/${id}`);
  }

  async DeleteQRCode(id: string): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.delete(`/delete-qr/${id}`);
  }
  async DeleteShortLink(
    id: string
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.delete(`/delete-url/${id}`);
  }

  async GenerateShortUrl(data: {
    url: string;
    name?: string;
    shortId?: string;
  }): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post("/short-url", data);
  }

  async FetchLinks(): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get("/links");
  }

  async FetchLinkById(
    id: string
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get(`/click/${id}`);
  }

  async GetOrder(
    amountId: string
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.get(`/order/${amountId}`);
  }

  async CapturePayment(
    amountId: string,
    razorpayResponse: {
      razorpay_payment_id: string;
      razorpay_order_id: string;
      razorpay_signature: string;
    }
  ): Promise<AxiosResponse<SuccessResponse, any>> {
    return await api.post(`capture/${amountId}`, {
      ...razorpayResponse,
    });
  }
}

export default new ApiService();
